/**
 * @file AskMeetingsPanel.tsx
 * @brief Ask a question across meetings (or one) and get a grounded, cited answer.
 *
 * @details
 * Two scopes, one component: pass `meetingId` to ask a single meeting, omit it
 * to ask everything the viewer can see. The backend difference is a WHERE
 * clause, so there is no reason for two UIs.
 *
 * The design principle carried over from evidence anchors: **an answer we do not
 * have must not look like one we do.** Three states are rendered distinctly —
 * answered, explicitly not-answered (the meetings do not cover it), and nothing
 * found at all. Only an answered state shows citations, and only cited excerpts
 * offer audio, because those are the ones the backend signed a URL for.
 *
 * Asking is a mutation, not a query: it consumes the plan's search allowance, so
 * it fires on submit and never on mount, focus, or a cache refetch.
 *
 * Layout is end-to-end GridBlock chrome (same hairline language as the filters
 * and the meetings table below): ask form → Answer|Related tab cells → result
 * cell. No floating card around the section. Citations stay on the Answer tab.
 * Mobile keeps a single scrolling screen. (03-08-2026)
 *
 * @dependencies
 * - @tanstack/react-query (via useAskMeetings)
 * - components/ui/{Button, Input, AudioPlayer}
 * - features/analytics/components/GridBlock
 *
 * @references
 * - BACKEND/src/features/boardroom/routes.js (POST /sessions/search)
 * - BACKEND/src/features/boardroom/meetingAnswer.service.mjs
 * - FRONTEND/.../MeetingIntelligence/AskMeetingsScreen.tsx (mobile twin)
 * - CompanyMeetingsFilters.tsx (flush grid control pattern)
 *
 * @author DES
 * @date 03-08-2026
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  CircleAlert,
  CircleHelp,
  Copy,
  Lock,
  Search,
  Sparkles,
  Volume1,
} from "lucide-react";
import { AudioPlayBadge } from "@/components/ui/AudioPlayer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import { isPastHistory } from "@/shared/history/historyLock";
import { useHistoryFrom } from "@/shared/history/useHistoryFrom";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { useAskMeetings, useMeetingSearchQuota } from "../api";
import { askAnswerBlocks, askAnswerDir } from "../askAnswerBlocks";
import {
  ASK_LIMIT_REACHED,
  askBlocked,
  askErrorText,
  askQuotaLine,
  askResetDate,
  askUpgradeLine,
} from "../askErrors";
import { MeetingSearchQuotaSchema, type MeetingSearchResult } from "../schemas";
import { useAuth } from "@/shared/auth/auth-context";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";

/** Flush control — matches CompanyMeetingsFilters so the ask row reads as one grid. */
const flatControlClass =
  "h-11 w-full rounded-none border-0 bg-transparent px-4 shadow-none ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";

/** Seconds → mm:ss, matching the voice-stamp labels elsewhere in Meeting Intel. */
function clock(secs: number) {
  const s = Math.max(0, Math.round(secs));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Playback window for a cited excerpt. Mirrors the meeting detail's rules —
 * small lead-in, bounded length — so the same moment sounds the same wherever
 * it is played. Related: MeetingDetailView (VOICE_STAMP_* constants).
 */
function clipWindow(anchor: MeetingSearchResult["anchor"]) {
  // An item the resolver could not place is stored as the UNANCHORED shape —
  // `{ startSecs: null, confidence: "none" }` — not as a missing anchor. Both
  // checks are load-bearing: `Number(null)` is 0, which is a REAL second, so
  // without them an unlocatable citation played the meeting's opening six
  // seconds under a confident "00:00". Nothing to point at must mean nothing to
  // play. (23-08-2026; docs/product/meeting-intel-evidence-anchor-plan.md)
  if (!anchor || anchor.confidence === "none") return null;
  if (anchor.startSecs == null) return null;
  const start = Number(anchor.startSecs);
  if (!Number.isFinite(start) || start < 0) return null;
  const end = anchor.endSecs == null ? NaN : Number(anchor.endSecs);
  const from = Math.max(0, start - 1.5);
  const raw = Number.isFinite(end) && end > start ? end + 2 - from : 12;
  return { from, secs: Math.min(45, Math.max(6, raw)) };
}

/**
 * The answer as plain-text blocks: "- " lines as a bulleted list, other lines
 * kept on their own line. Direction is set for the whole answer so Urdu reads
 * right-aligned with its bullets on the right. (16-10-2026)
 */
function AnswerText({ text, className }: { text: string; className?: string }) {
  const blocks = askAnswerBlocks(text);
  return (
    <div dir={askAnswerDir(text)} className={cn("space-y-2 text-sm", className)}>
      {blocks.map((block, i) =>
        block.kind === "list" ? (
          <ul key={i} className="m-0 list-disc space-y-1 ps-5 marker:text-brand">
            {block.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i} className="m-0">
            {block.lines.map((line, j) => (
              <span key={j} className="block">
                {line}
              </span>
            ))}
          </p>
        ),
      )}
    </div>
  );
}

/**
 * Copy the answer as plain text (DES 21-10-2026: "create me a follow-up
 * email" → paste it straight into Mail). Ticks for a moment once copied.
 */
export function CopyAnswer({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text.trim());
      setCopied(true);
    } catch {
      toast({ variant: "destructive", title: "Couldn't copy", description: "Please try again." });
    }
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => void copy()}
      aria-label={copied ? "Answer copied" : "Copy answer"}
      className="h-7 gap-1.5 rounded-full px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function CitationRow({
  item,
  meetingHref,
}: {
  item: MeetingSearchResult;
  meetingHref: (id: string) => string;
}) {
  const win = clipWindow(item.anchor);
  const canPlay = Boolean(item.recordingUrl && win);
  const approx = item.anchor?.confidence === "approx";
  // B39: the meeting is older than this user's history window; the link
  // opens a detail that says so.
  const pastHistory = isPastHistory(item.startedAt, useHistoryFrom());

  return (
    <li className="flex items-start gap-3 border-b border-border/50 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-foreground/90">{item.content}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <Link
            to={meetingHref(item.meetingId)}
            className="font-medium text-brand hover:underline"
          >
            {item.meetingTitle || "Meeting"}
          </Link>
          {pastHistory ? (
            <Lock className="h-3 w-3 shrink-0" strokeWidth={1.75} aria-label="Older than your plan keeps" />
          ) : null}
          {item.startedAt ? (
            <span>· {new Date(item.startedAt).toLocaleDateString()}</span>
          ) : null}
          {/* Kind reads as a pill, matching the app's rows — the same moment
              should look like the same thing on both surfaces. Deliberately
              NEUTRAL, not brand-tinted: the play control on this row owns the
              brand colour, and a second tinted element would read as clickable.
              (03-08-2026) */}
          {item.kind ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              {String(item.kind).replace(/_/g, " ")}
            </span>
          ) : null}
          {win ? (
            // "~" carries the same meaning as on the meeting detail rows: the
            // span was interpolated, not measured.
            <span className="font-mono tabular-nums">
              · {approx ? "~" : ""}
              {clock(Number(item.anchor?.startSecs))}
            </span>
          ) : null}
        </div>
      </div>
      {canPlay && win ? (
        <AudioPlayBadge
          src={item.recordingUrl}
          variant="original"
          startSecs={win.from}
          clipSecs={win.secs}
          icon={<Volume1 className="h-3.5 w-3.5" />}
          title="Play the moment this came from"
          className="h-8 w-8 shrink-0 justify-center border-transparent bg-muted p-0 text-foreground/55 hover:bg-brand-soft hover:text-brand"
          playingClassName="border-brand bg-brand text-brand-foreground"
        />
      ) : null}
    </li>
  );
}

type ResultTab = "answer" | "related";

export function AskMeetingsPanel({
  meetingId = null,
  meetingHref = (id: string) => `/company/meetings/${id}`,
  className,
}: {
  /** Restrict to one meeting. Omit to ask across everything the viewer can see. */
  meetingId?: string | null;
  meetingHref?: (id: string) => string;
  className?: string;
}) {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<ResultTab>("answer");
  const ask = useAskMeetings();
  const data = ask.data;
  const quotaQuery = useMeetingSearchQuota();
  const { user } = useAuth();
  // The allowance (22-10-2026): the code, not any 429; its quota (remaining
  // 0) wins, so the box turns off at once.
  const errorCode = ask.error instanceof ApiClientError ? ask.error.code : undefined;
  const limitQuota = (() => {
    if (errorCode !== ASK_LIMIT_REACHED || !(ask.error instanceof ApiClientError)) return null;
    const raw = (ask.error.raw as { response?: { data?: { quota?: unknown } } } | undefined)?.response
      ?.data?.quota;
    const parsed = MeetingSearchQuotaSchema.safeParse(raw ?? {});
    return parsed.success ? { ...parsed.data, remaining: parsed.data.remaining ?? 0 } : { remaining: 0 };
  })();
  const shownQuota = limitQuota ?? data?.quota ?? quotaQuery.data?.quota ?? null;
  const quotaLine = askQuotaLine(shownQuota);
  const blocked = askBlocked(shownQuota);
  const resetDate = askResetDate(shownQuota?.periodEnd);
  const upgrade = askUpgradeLine(shownQuota, billingAudienceFor(user));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const question = q.trim();
    if (!question || ask.isPending || blocked) return;
    ask.mutate({ q: question, meetingId });
  };

  const answered = data?.answer?.answered === true;
  const refused = Boolean(data?.answer && data.answer.answered === false);
  const nothingFound = Boolean(data && data.results.length === 0);
  const related = data?.results?.slice(0, 8) ?? [];
  const hasAnswerPane = Boolean(answered || refused || nothingFound);
  const hasRelatedPane = related.length > 0;
  // Tabs only when both panes have something — otherwise the lone pane stands alone.
  const showTabs = hasAnswerPane && hasRelatedPane;
  const showResults = hasAnswerPane || hasRelatedPane;

  // Fresh ask → land on Answer when there is one (including an honest refusal);
  // otherwise open Related. Related: meetingAnswer.service.mjs
  useEffect(() => {
    if (!data) return;
    if (data.answer) {
      setTab("answer");
    } else if (data.results.length > 0) {
      setTab("related");
    }
  }, [data]);

  const answerPane = (
    <>
      {answered ? (
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              Answer
            </span>
            {data?.answer?.text ? <CopyAnswer text={data.answer.text} /> : null}
          </div>
          <AnswerText
            text={data?.answer?.text ?? ""}
            className="leading-relaxed text-foreground"
          />

          {data?.answer?.citations?.length ? (
            <div className="mt-4 border-t border-brand/20 pt-1">
              <p className="mb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                From these moments
              </p>
              <ul className="m-0 list-none p-0">
                {data.answer.citations.map((c, i) => (
                  <CitationRow key={i} item={c} meetingHref={meetingHref} />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Explicitly NOT answered. Deliberately plain — no brand tint, no
          citations — so it can never be mistaken for an answer. */}
      {refused ? (
        <div className="flex items-start gap-2">
          <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <AnswerText
            text={data?.answer?.text || "These meetings do not cover that."}
            className="text-muted-foreground"
          />
        </div>
      ) : null}

      {nothingFound ? (
        <p className="text-sm text-muted-foreground">
          Nothing in {meetingId ? "this meeting" : "your meetings"} matches that
          question.
        </p>
      ) : null}
    </>
  );

  const relatedPane = hasRelatedPane ? (
    <ul className="m-0 list-none p-0">
      {related.map((r, i) => (
        <CitationRow key={i} item={r} meetingHref={meetingHref} />
      ))}
    </ul>
  ) : null;

  const resultBody = showTabs
    ? tab === "answer"
      ? answerPane
      : relatedPane
    : (
        <>
          {hasAnswerPane ? answerPane : null}
          {hasRelatedPane ? (
            <div className={hasAnswerPane ? "mt-4 border-t border-border/60 pt-3" : undefined}>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {answered ? "Other related moments" : "Closest matches"}
              </p>
              {relatedPane}
            </div>
          ) : null}
        </>
      );

  // Answered content wears brand tint on the CELL, not a nested card — the grid
  // already frames it. Refusal / empty stay muted so they cannot skim-read as
  // answers. Related: evidence-anchor honesty rule.
  const resultCellTone =
    showTabs && tab === "answer" && answered
      ? "bg-brand-soft/30"
      : !showTabs && answered
        ? "bg-brand-soft/30"
        : undefined;

  return (
    <div className={className}>
      <form onSubmit={submit}>
        <GridCells className="grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto]">
          {/* Soft brand wash — the ask row sits above white filter cells, so a
              plain transparent field disappeared into the page. Related: filters
              stay muted-neutral; this one signals "ask", not "filter". */}
          <GridCell padded={false} className="bg-brand-soft/40 sm:col-span-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                maxLength={500}
                disabled={blocked}
                placeholder={
                  blocked
                    ? resetDate
                      ? `No questions left until ${resetDate}`
                      : "No questions left this period"
                    : meetingId
                    ? "Ask this meeting… e.g. what was decided about the timeline?"
                    : "Ask your meetings… e.g. what did we agree with this client about pricing?"
                }
                className={cn(flatControlClass, "bg-transparent pl-10")}
              />
            </div>
          </GridCell>
          <GridCell padded={false} className="flex bg-brand-soft/40">
            <Button
              type="submit"
              disabled={!q.trim() || ask.isPending || blocked}
              className="h-11 w-full rounded-none px-6 sm:min-w-[5.5rem]"
            >
              {ask.isPending ? "Asking…" : "Ask"}
            </Button>
          </GridCell>
        </GridCells>
      </form>

      {ask.isError ? (
        <GridCell className="bg-destructive/[0.06]">
          <div className="flex items-start gap-2 text-sm">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p>
              {ask.error instanceof ApiClientError
                ? askErrorText(ask.error.status, ask.error.message, errorCode, limitQuota)
                : askErrorText(undefined)}
            </p>
          </div>
        </GridCell>
      ) : null}

      {showTabs ? (
        <GridCells className="grid-cols-2" role="tablist" aria-label="Ask AI results">
          <GridCell padded={false}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "answer"}
              onClick={() => setTab("answer")}
              className={cn(
                "flex h-full w-full items-center justify-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors",
                tab === "answer"
                  ? "bg-brand-soft text-brand-soft-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Answer
            </button>
          </GridCell>
          <GridCell padded={false}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "related"}
              onClick={() => setTab("related")}
              className={cn(
                "flex h-full w-full items-center justify-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors",
                tab === "related"
                  ? "bg-brand-soft text-brand-soft-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {answered ? "Related" : "Closest matches"}
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                  tab === "related"
                    ? "bg-brand/15 text-brand"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {related.length}
              </span>
            </button>
          </GridCell>
        </GridCells>
      ) : null}

      {showResults ? (
        <GridCell className={resultCellTone} role="tabpanel">
          {resultBody}
        </GridCell>
      ) : null}

      {/* Shown from the moment the panel loads, not only after a search: the
          allowance used to arrive solely inside a search response, so the only
          way to learn how many you had left was to spend one. A response
          carries a fresher count, so it wins once one exists. (03-08-2026) */}
      {/* Only while the limit holds (Free always; 22-10-2026): never "not
          enforced yet". At 0 the box above is off and the limit error, or
          this line, says when it resets. */}
      {quotaLine ? (
        <GridCell padded={false}>
          <p className="flex flex-wrap items-center gap-x-2 px-4 py-2 text-[11px] text-muted-foreground md:px-5">
            <span>{quotaLine}</span>
            {/* The way out, web + Mac only (the phone shows no link). */}
            {upgrade?.href ? (
              <Link to={upgrade.href} className="font-semibold text-brand hover:underline">
                {upgrade.text}
              </Link>
            ) : upgrade ? (
              <span>{upgrade.text}</span>
            ) : null}
          </p>
        </GridCell>
      ) : null}
    </div>
  );
}
